import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { theme } from '../../styles/theme';
import api from '../../config/api';
import {
  XMarkIcon,
  MagnifyingGlassIcon,
  CpuChipIcon,
  CircleStackIcon,
  SignalIcon,
  PlayIcon,
  PlusIcon
} from '@heroicons/react/24/outline';
import DataBrowser from './DataBrowser';
import SignalKCatalogPicker from '../signalk/SignalKCatalogPicker';
import { resolveWidgetDataPath } from '../../utils/signalKValueFormat';
import { SIGNALK_SOURCE_LABELS, SIGNALK_SOURCE_PRIORITY } from '../../utils/signalkWidgetData';
import { normalizeWidgetDemoConfig } from '../../utils/widgetDemoConfig';
import { sectionDisplayName } from '../../utils/dashboardSectionUtils';
import { isTemperaturePath } from '../../utils/dashboardWidgetUtils';
import { widgetWidthOptionsForSection, clampSectionColumns } from '../../utils/dashboardGridUtils';
import {
  isStatusDataPath,
  defaultBalancerStatusRanges,
} from '../../utils/dashboardStatusAlarmUtils';
import toast from 'react-hot-toast';

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
`;

const ModalContent = styled.div`
  background: linear-gradient(135deg, #1e293b 0%, #334155 100%);
  border: 1px solid #475569;
  border-radius: 12px;
  padding: 0;
  width: 95%;
  max-width: 1200px;
  height: 90vh;
  max-height: 90vh;
  overflow: hidden;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
  display: flex;
  flex-direction: column;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem 2rem;
  border-bottom: 1px solid #475569;
  background: rgba(0, 0, 0, 0.2);
`;

const ModalTitle = styled.h2`
  color: #ffffff;
  font-size: 20px;
  font-weight: 600;
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: #94a3b8;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 6px;
  
  &:hover {
    background: rgba(255, 255, 255, 0.1);
    color: #ffffff;
  }
`;

const TabContainer = styled.div`
  display: flex;
  border-bottom: 1px solid #475569;
  background: rgba(0, 0, 0, 0.1);
`;

const TabButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.625rem 1rem;
  border: none;
  background: none;
  color: #94a3b8;
  cursor: pointer;
  border-bottom: 2px solid transparent;
  transition: all 0.2s ease;
  font-size: 13px;
  font-weight: 500;
  white-space: nowrap;

  svg {
    width: 1rem;
    height: 1rem;
    flex-shrink: 0;
  }
  
  ${props => props.$active && `
    color: #3b82f6;
    border-bottom-color: #3b82f6;
    background: rgba(59, 130, 246, 0.1);
  `}
  
  &:hover {
    color: #ffffff;
    background: rgba(255, 255, 255, 0.05);
  }
`;

const TabContent = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

const FormContainer = styled.div`
  padding: 1.5rem;
  overflow-y: auto;
  flex: 1;
  background: rgba(0, 0, 0, 0.1);
`;

const TabHint = styled.p`
  margin: 0;
  padding: 0.5rem 1rem;
  font-size: 12px;
  color: #94a3b8;
  background: rgba(0, 0, 0, 0.15);
  border-bottom: 1px solid #475569;
  line-height: 1.45;
  flex-shrink: 0;
`;

const DataBrowserContainer = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  overflow: hidden;
  display: ${(props) => (props.$hidden ? 'none' : 'flex')};
  flex-direction: column;
  padding: 0.75rem 1rem 1rem;
`;

const FormGroup = styled.div`
  margin-bottom: 1rem;
`;

const FormSection = styled.div`
  background: rgba(0, 0, 0, 0.2);
  border: 1px solid #475569;
  border-radius: 8px;
  padding: 1.25rem;
  margin-bottom: 1.5rem;
`;

const SectionTitle = styled.h3`
  color: #3b82f6;
  font-size: 14px;
  font-weight: 600;
  margin: 0 0 1rem 0;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const Label = styled.label`
  display: block;
  color: #ffffff;
  font-size: 14px;
  font-weight: 500;
  margin-bottom: 0.5rem;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #475569;
  border-radius: 8px;
  background: #1e293b;
  color: #ffffff;
  font-size: 14px;
  
  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
  
  &::placeholder {
    color: #64748b;
  }
`;

const DataPathRow = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: stretch;
`;

const PathPickerButton = styled.button`
  flex: 1;
  text-align: left;
  padding: 0.75rem;
  border: 1px solid #475569;
  border-radius: 8px;
  background: #1e293b;
  color: ${(props) => (props.$empty ? '#64748b' : '#ffffff')};
  font-size: 14px;
  cursor: pointer;
  font-family: inherit;
  min-height: 44px;

  &:hover {
    border-color: #3b82f6;
    background: #0f172a;
  }

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;

const BrowseDataButton = styled.button`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0 1rem;
  border: 1px solid #3b82f6;
  border-radius: 8px;
  background: #2563eb;
  color: #ffffff;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;

  svg {
    width: 1rem;
    height: 1rem;
  }

  &:hover {
    background: #1d4ed8;
  }
`;

const ManualPathToggle = styled.button`
  margin-top: 0.5rem;
  padding: 0;
  border: none;
  background: none;
  color: #94a3b8;
  font-size: 12px;
  cursor: pointer;
  text-decoration: underline;

  &:hover {
    color: #ffffff;
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #475569;
  border-radius: 8px;
  background: #1e293b;
  color: #ffffff;
  font-size: 14px;
  
  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
  
  option {
    background: #1e293b;
    color: #ffffff;
  }
`;

const ConfigGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  flex-shrink: 0;
  padding: 1rem 2rem;
  border-top: 1px solid #475569;
  background: rgba(0, 0, 0, 0.2);
`;

const Button = styled.button`
  padding: 0.75rem 1.5rem;
  border-radius: ${theme.borderRadius.md};
  font-size: ${theme.fontSizes.sm};
  font-weight: ${theme.fontWeights.medium};
  cursor: pointer;
  transition: all 0.2s ease;
  
  ${props => props.variant === 'primary' ? `
    background: ${theme.colors.primary.main};
    color: white;
    border: 1px solid ${theme.colors.primary.main};
    
    &:hover {
      background: ${theme.colors.primary.dark};
    }
  ` : `
    background: ${theme.colors.background.paper};
    color: ${theme.colors.text.primary};
    border: 1px solid ${theme.colors.border.secondary};
    
    &:hover {
      background: ${theme.colors.background.secondary};
    }
  `}
`;

const WidgetEditor = ({ widget, sections, onSave, onClose, vesselId = 1, defaultSectionId = null, dashboardId = null }) => {
  const [activeTab, setActiveTab] = useState('settings');
  const [showManualPath, setShowManualPath] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    type: 'digital',
    dataSource: '',
    width: 'third',
    sectionId: null,
    config: {
      unit: '',
      min: 0,
      max: 100,
      decimals: 1,
      refreshRate: 30,
      forceDemo: false,
      colorRanges: [{ id: 1, min: 0, max: 100, color: '#10b981', label: 'Normal' }],
      statusRanges: [],
      yellowAlarm: false,
      redAlarm: false,
      alarmEmails: ''
    }
  });

  useEffect(() => {
    console.log('🔧 WidgetEditor useEffect - widget:', widget, 'defaultSectionId:', defaultSectionId);
    
    if (widget) {
      const sectionId = widget.sectionId || defaultSectionId || null;
      console.log('🔧 Setting sectionId to:', sectionId);
      
      // Convert old colorRanges format (object) to new format (array)
      let colorRanges = [{ id: 1, min: 0, max: 100, color: '#10b981', label: 'Normal' }];
      
      if (widget.config?.colorRanges) {
        if (Array.isArray(widget.config.colorRanges)) {
          // Already in new array format
          colorRanges = widget.config.colorRanges;
        } else if (typeof widget.config.colorRanges === 'object') {
          // Old object format - convert to array
          colorRanges = [];
          let id = 1;
          
          // Convert green ranges
          if (widget.config.colorRanges.green?.length > 0) {
            colorRanges.push(...widget.config.colorRanges.green.map(r => ({
              ...r,
              color: r.color || '#10b981',
              label: r.label || 'Normal'
            })));
          }
          
          // Convert yellow ranges
          if (widget.config.colorRanges.yellow?.length > 0) {
            colorRanges.push(...widget.config.colorRanges.yellow.map(r => ({
              ...r,
              color: r.color || '#f59e0b',
              label: r.label || 'Warning'
            })));
          }
          
          // Convert red ranges
          if (widget.config.colorRanges.red?.length > 0) {
            colorRanges.push(...widget.config.colorRanges.red.map(r => ({
              ...r,
              color: r.color || '#ef4444',
              label: r.label || 'Alarm'
            })));
          }
          
          // If no ranges were converted, use default
          if (colorRanges.length === 0) {
            colorRanges = [{ id: 1, min: 0, max: 100, color: '#10b981', label: 'Normal' }];
          }
        }
      }

      let statusRanges = [];
      if (Array.isArray(widget.config?.statusRanges)) {
        statusRanges = widget.config.statusRanges;
      } else if (isStatusDataPath(resolveWidgetDataPath(widget))) {
        statusRanges = defaultBalancerStatusRanges();
      }
      
      const isNewWidget = !widget.id; // New widgets have no id
      const initialType = widget.type || widget.widget_type || 'digital';
      const resolvedType = isNewWidget ? 'digital' : initialType; // Force digital for new widgets

      const normalizedConfig = normalizeWidgetDemoConfig(
        widget.config || widget.display_config || {}
      );

      setFormData({
        name: widget.name || widget.widget_name || '',
        type: resolvedType,
        dataSource: resolveWidgetDataPath(widget),
        width: widget.width || normalizedConfig.layoutWidth || widget.config?.layoutWidth || 'third',
        sectionId: sectionId,
        config: {
          ...normalizedConfig,
          unit: normalizedConfig.unit || widget.config?.unit || '',
          min: normalizedConfig.min ?? widget.config?.min ?? 0,
          max: normalizedConfig.max ?? widget.config?.max ?? 100,
          decimals: normalizedConfig.decimals ?? widget.config?.decimals ?? 1,
          heightPx: normalizedConfig.heightPx || widget.config?.heightPx || 120,
          refreshRate: normalizedConfig.refreshRate || widget.config?.refreshRate || 30,
          gaugeType: normalizedConfig.gaugeType || widget.config?.gaugeType || 'circular',
          widthPercent: normalizedConfig.widthPercent || widget.config?.widthPercent || 50,
          colorRanges: colorRanges,
          statusRanges,
          yellowAlarm: normalizedConfig.yellowAlarm || widget.config?.yellowAlarm || false,
          redAlarm: normalizedConfig.redAlarm || widget.config?.redAlarm || false,
          alarmEmails: normalizedConfig.alarmEmails || widget.config?.alarmEmails || '',
        }
      });
    } else if (defaultSectionId) {
      // If no widget but we have a default section, pre-select it with digital as default
      console.log('🔧 Setting default sectionId to:', defaultSectionId);
      setFormData(prev => ({
        ...prev,
        type: prev.type || 'digital', // Default to 'digital' for new widgets
        sectionId: defaultSectionId
      }));
    }
  }, [widget, defaultSectionId, vesselId]);

  const handleSubmit = (e) => {
    if (e) e.preventDefault();
    
    // Clean up colorRanges - convert empty strings to 0
    const cleanedColorRanges = formData.config.colorRanges.map(range => ({
      ...range,
      min: range.min === '' ? 0 : parseFloat(range.min) || 0,
      max: range.max === '' ? 0 : parseFloat(range.max) || 0
    }));
    
    const dataPath = formData.dataSource || '';
    const cleanedStatusRanges = (formData.config.statusRanges || []).map((range) => ({
      ...range,
      match: String(range.match || '').trim(),
    }));

    const widgetConfig = normalizeWidgetDemoConfig({
      ...formData.config,
      colorRanges: cleanedColorRanges,
      statusRanges: cleanedStatusRanges,
      ...(dataPath ? { dataPath } : {}),
    });
    if (formData.type !== 'gauge') {
      delete widgetConfig.gaugeType;
    }

    const updatedWidget = {
      ...widget,
      name: formData.name,
      type: formData.type,
      width: formData.width,
      sectionId: formData.sectionId,
      dataSource: dataPath,
      data_path: dataPath,
      dataPath: dataPath,
      data_source: 'signalk',
      config: widgetConfig,
    };

    onSave(updatedWidget);
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleConfigChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      config: {
        ...prev.config,
        [field]: value
      }
    }));
  };

  const handleColorRangeChange = (rangeId, field, value) => {
    setFormData(prev => ({
      ...prev,
      config: {
        ...prev.config,
        colorRanges: prev.config.colorRanges.map(range => 
          range.id === rangeId ? { 
            ...range, 
            [field]: value 
          } : range
        )
      }
    }));
  };

  const addColorRange = () => {
    const newId = Math.max(...formData.config.colorRanges.map(r => r.id), 0) + 1;
    setFormData(prev => ({
      ...prev,
      config: {
        ...prev.config,
        colorRanges: [
          ...prev.config.colorRanges,
          { id: newId, min: 0, max: 0, color: '#10b981', label: 'Range' }
        ]
      }
    }));
  };

  const removeColorRange = (rangeId) => {
    setFormData(prev => ({
      ...prev,
      config: {
        ...prev.config,
        colorRanges: prev.config.colorRanges.filter(range => range.id !== rangeId)
      }
    }));
  };

  const handleStatusRangeChange = (rangeId, field, value) => {
    setFormData(prev => ({
      ...prev,
      config: {
        ...prev.config,
        statusRanges: prev.config.statusRanges.map((range) =>
          range.id === rangeId ? { ...range, [field]: value } : range
        ),
      },
    }));
  };

  const addStatusRange = () => {
    const ranges = formData.config.statusRanges || [];
    const newId = Math.max(...ranges.map((r) => r.id), 0) + 1;
    setFormData((prev) => ({
      ...prev,
      config: {
        ...prev.config,
        statusRanges: [
          ...ranges,
          { id: newId, match: '', color: '#3b82f6', label: 'Status', alarm: 'none' },
        ],
      },
    }));
  };

  const removeStatusRange = (rangeId) => {
    setFormData((prev) => ({
      ...prev,
      config: {
        ...prev.config,
        statusRanges: (prev.config.statusRanges || []).filter((range) => range.id !== rangeId),
      },
    }));
  };

  const applyBalancerStatusPresets = () => {
    setFormData((prev) => ({
      ...prev,
      config: {
        ...prev.config,
        statusRanges: defaultBalancerStatusRanges(),
      },
    }));
    toast.success('Balancer status presets applied');
  };

  const handleDataPathSelect = (path, data) => {
    const value = data?.value || data?.value?.value || data;

    setFormData(prev => {
      const isStatusPath = isStatusDataPath(path);
      const nextConfig = { ...prev.config };
      if (isStatusPath && (!nextConfig.statusRanges || nextConfig.statusRanges.length === 0)) {
        nextConfig.statusRanges = defaultBalancerStatusRanges();
      }
      if (isStatusPath && path.includes('balancer') && !nextConfig.redAlarm) {
        nextConfig.redAlarm = true;
      }
      return {
        ...prev,
        dataSource: path,
        name: prev.name || getDisplayNameForPath(path),
        type: prev.type || getSuggestedWidgetType(path, value),
        config: nextConfig,
      };
    });
    setActiveTab('settings');
    toast.success('Path selected — review settings and save');
  };

  const openPathPicker = (tab) => setActiveTab(tab);

  const getAvailableTabs = () => [
    { id: 'settings', label: 'Settings', icon: 'CpuChipIcon' },
    { id: 'browse', label: 'Tree browse', icon: 'DatabaseIcon' },
    { id: 'catalog', label: 'Path catalog', icon: 'SignalIcon' },
  ];

  const pathPickerHint =
    activeTab === 'browse'
      ? 'Tree view — expand branches (electrical → batteries → …) and click a value or Use path.'
      : activeTab === 'catalog'
        ? 'Searchable list of every path on the boat — best when you know the name (e.g. chargingMode, inverter). Click Use path.'
        : '';

  const getDisplayNameForPath = (path) => {
    const parts = path.split('.');
    const lastPart = parts[parts.length - 1];
    return lastPart
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, str => str.toUpperCase())
      .trim();
  };

  const getSuggestedWidgetType = (path, value) => {
    // Default to Number display unless a specialized vis is clearly required
    if (path.includes('level') || path.includes('ratio')) return 'progress';
    if (path.includes('heading') || path.includes('direction')) return 'compass';
    if (path.includes('position')) return 'map';
    return 'digital';
  };

  const showStatusRanges =
    isStatusDataPath(formData.dataSource) ||
    (formData.config.statusRanges && formData.config.statusRanges.length > 0);

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>Edit Widget</ModalTitle>
          <CloseButton onClick={onClose}>
            <XMarkIcon className="w-5 h-5" />
          </CloseButton>
        </ModalHeader>

        <TabContainer>
          {getAvailableTabs().map(tab => {
            let IconComponent;
            
            if (typeof tab.icon === 'function') {
              // It's a React component
              IconComponent = tab.icon;
            } else {
              // It's a string reference
              IconComponent = {
                'CpuChipIcon': CpuChipIcon,
                'DatabaseIcon': CircleStackIcon,
                'SignalIcon': SignalIcon,
                'PlayIcon': PlayIcon
              }[tab.icon] || CpuChipIcon;
            }
            
            return (
              <TabButton 
                key={tab.id}
                type="button"
                $active={activeTab === tab.id} 
                onClick={() => setActiveTab(tab.id)}
              >
                <IconComponent />
                {tab.label}
              </TabButton>
            );
          })}
        </TabContainer>

        {pathPickerHint ? <TabHint>{pathPickerHint}</TabHint> : null}

        <TabContent>
          {activeTab === 'settings' && (
            <FormContainer>
              <form onSubmit={handleSubmit}>
                {/* Basic Information */}
                <FormSection>
                  <SectionTitle>Basic Information</SectionTitle>
            <FormGroup>
              <Label>Widget Name</Label>
              <Input
                type="text"
                      value={formData.name}
                      onChange={(e) => handleChange('name', e.target.value)}
                      placeholder="Enter widget name"
              />
            </FormGroup>

                  <ConfigGrid>
            <FormGroup>
                      <Label>Widget Type</Label>
              <Select
                        value={formData.type}
                        onChange={(e) => {
                          const newType = e.target.value;
                          setFormData((prev) => {
                            const nextConfig = { ...prev.config };
                            if (newType !== 'gauge') {
                              delete nextConfig.gaugeType;
                            } else if (!nextConfig.gaugeType) {
                              nextConfig.gaugeType = 'circular';
                            }
                            return { ...prev, type: newType, config: nextConfig };
                          });
                        }}
                      >
                        <option value="digital">Number Display</option>
                        <option value="gauge">Gauge</option>
                        <option value="bar">Bar Chart</option>
                        <option value="text">Text Display</option>
              </Select>
            </FormGroup>

                    {formData.type === 'gauge' && (
              <FormGroup>
                        <Label>Gauge Style</Label>
                <Select
                          value={formData.config?.gaugeType || 'circular'}
                          onChange={(e) => handleConfigChange('gaugeType', e.target.value)}
                        >
                          <option value="circular">Circular Gauge</option>
                          <option value="linear">Linear Gauge</option>
                          <option value="tank">Tank Level</option>
                </Select>
              </FormGroup>
            )}
                  </ConfigGrid>
                </FormSection>

                {/* Layout & Positioning */}
                <FormSection>
                  <SectionTitle>Layout & Positioning</SectionTitle>
                  <ConfigGrid>
              <FormGroup>
                      <Label>Section</Label>
                <Select
                        value={formData.sectionId || ''}
                        onChange={(e) => handleChange('sectionId', e.target.value ? parseInt(e.target.value) : null)}
                      >
                        <option value="">Select a section</option>
                        {(sections || []).map(section => (
                          <option key={section.id} value={section.id}>
                            {sectionDisplayName(section)}
                    </option>
                  ))}
                </Select>
              </FormGroup>

              <FormGroup>
                      <Label>Widget width in section</Label>
                <Select
                        value={formData.width}
                        onChange={(e) => handleChange('width', e.target.value)}
                      >
                        {widgetWidthOptionsForSection(
                          clampSectionColumns(
                            (sections || []).find((s) => String(s.id) === String(formData.sectionId))
                              ?.columns
                              ?? (sections || []).find((s) => String(s.id) === String(formData.sectionId))
                                ?.max_widgets_per_row
                              ?? 2
                          )
                        ).map((opt) => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                </Select>
              </FormGroup>
                  </ConfigGrid>
                </FormSection>

                {/* Data Source */}
                <FormSection>
                  <SectionTitle>Data Source</SectionTitle>
              <FormGroup>
                    <Label id="widget-data-path-label">SignalK Data Path</Label>
                    <DataPathRow>
                      <PathPickerButton
                        type="button"
                        $empty={!formData.dataSource}
                        onClick={() => openPathPicker('browse')}
                        aria-labelledby="widget-data-path-label"
                      >
                        {formData.dataSource || 'Click to browse N2K / SignalK data…'}
                      </PathPickerButton>
                      <BrowseDataButton type="button" onClick={() => openPathPicker('catalog')}>
                        <SignalIcon />
                        Catalog
                      </BrowseDataButton>
                      <BrowseDataButton type="button" onClick={() => openPathPicker('browse')}>
                        <CircleStackIcon />
                        Tree
                      </BrowseDataButton>
                    </DataPathRow>
                    {!showManualPath ? (
                      <ManualPathToggle type="button" onClick={() => setShowManualPath(true)}>
                        Type path manually
                      </ManualPathToggle>
                    ) : (
                      <div style={{ marginTop: '0.5rem' }}>
                        <Input
                          id="widget-data-path"
                          type="text"
                          value={formData.dataSource}
                          onChange={(e) => handleChange('dataSource', e.target.value)}
                          placeholder="electrical.batteries.279-second.voltage"
                          autoFocus
                        />
                      </div>
                    )}
                    <div style={{ 
                      fontSize: theme.fontSizes.xs, 
                      color: theme.colors.text.secondary,
                      marginTop: '0.25rem' 
                    }}>
                      {formData.dataSource
                        ? `Selected: ${formData.dataSource}`
                        : 'Click the field or Browse to pick from your vessel data tree'}
                    </div>
              </FormGroup>

            <FormGroup>
                    <Label>Data Source</Label>
                    <div style={{
                      fontSize: theme.fontSizes.sm,
                      color: theme.colors.text.secondary,
                      lineHeight: 1.5,
                      padding: '0.75rem',
                      background: theme.colors.background.secondary,
                      borderRadius: theme.borderRadius.md,
                      border: `1px solid ${theme.colors.border.secondary}`,
                    }}>
                      <strong>Automatic.</strong> Widgets use the best available source in order:{' '}
                      {SIGNALK_SOURCE_PRIORITY.map((key) => SIGNALK_SOURCE_LABELS[key]).join(' → ')}.
                      The icon on each widget shows which source is active.
                    </div>
            </FormGroup>

            <FormGroup>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <input
                        type="checkbox"
                        id="forceDemo"
                        checked={formData.config.forceDemo || false}
                        onChange={(e) => handleConfigChange('forceDemo', e.target.checked)}
                        style={{ margin: 0 }}
                      />
                      <Label htmlFor="forceDemo" style={{ margin: 0 }}>
                        Force demo data (preview / showroom only)
                      </Label>
                    </div>
                    <div style={{
                      fontSize: theme.fontSizes.xs,
                      color: theme.colors.text.secondary,
                      marginTop: '0.25rem',
                      marginLeft: '1.5rem',
                    }}>
                      When enabled, this widget always uses sample data instead of live or stored vessel data.
                    </div>
            </FormGroup>
                </FormSection>

                {/* Display Configuration */}
                <FormSection>
                  <SectionTitle>Display Configuration</SectionTitle>
                  <ConfigGrid>
            <FormGroup>
              <Label>{isTemperaturePath(formData.dataSource) ? 'Temperature unit' : 'Unit'}</Label>
              {isTemperaturePath(formData.dataSource) ? (
                <Select
                  value={formData.config.unit || 'F'}
                  onChange={(e) => handleConfigChange('unit', e.target.value)}
                >
                  <option value="F">Fahrenheit (°F)</option>
                  <option value="C">Celsius (°C)</option>
                  <option value="K">Kelvin (K)</option>
                </Select>
              ) : (
                <Input
                  type="text"
                  value={formData.config.unit || ''}
                  onChange={(e) => handleConfigChange('unit', e.target.value)}
                  placeholder="e.g., V, A, kts"
                />
              )}
            </FormGroup>

            <FormGroup>
                    <Label>Min Value</Label>
              <Input
                type="number"
                      value={formData.config.min}
                      onChange={(e) => handleConfigChange('min', parseFloat(e.target.value) || 0)}
                      placeholder="0"
              />
            </FormGroup>

            <FormGroup>
                    <Label>Max Value</Label>
              <Input
                type="number"
                      value={formData.config.max}
                      onChange={(e) => handleConfigChange('max', parseFloat(e.target.value) || 100)}
                      placeholder="100"
              />
            </FormGroup>

                <FormGroup>
                    <Label>Decimal Places</Label>
                  <Input
                    type="number"
                      min="0"
                      max="10"
                      value={formData.config.decimals}
                      onChange={(e) => handleConfigChange('decimals', parseInt(e.target.value) || 1)}
                      placeholder="1"
                  />
                </FormGroup>


                <FormGroup>
                    <Label>Refresh Rate (seconds)</Label>
                  <Input
                    type="number"
                      min="1"
                      max="300"
                      value={formData.config.refreshRate}
                      onChange={(e) => handleConfigChange('refreshRate', parseInt(e.target.value) || 30)}
                      placeholder="30"
                  />
                </FormGroup>
                  </ConfigGrid>
                  
                  {/* Chart-specific options */}
                  {(formData.type === 'chart' || formData.type === 'line') && (
                    <FormGroup style={{ marginTop: '1rem' }}>
                      <Label>Chart Legend Label</Label>
                      <Input
                        type="text"
                        value={formData.config.chartLabel || ''}
                        onChange={(e) => handleConfigChange('chartLabel', e.target.value)}
                        placeholder="e.g., Battery Voltage, Engine Temp"
                      />
                <div style={{ 
                        fontSize: theme.fontSizes.xs, 
                  color: theme.colors.text.secondary, 
                        marginTop: '0.25rem' 
                }}>
                        Optional label to display in the chart legend
                </div>
                    </FormGroup>
                  )}
                </FormSection>

                {/* Color Ranges */}
                <FormSection>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                    <SectionTitle style={{ margin: 0 }}>Color Ranges</SectionTitle>
                    <button
                      type="button"
                      onClick={addColorRange}
                      style={{
                        background: '#3b82f6',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        padding: '0.5rem 1rem',
                        fontSize: '12px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem'
                      }}
                    >
                      <PlusIcon className="w-4 h-4" />
                      Add Range
                    </button>
                </div>
                  
                    <div style={{ 
                    background: 'rgba(0, 0, 0, 0.2)', 
                    padding: '1rem', 
                    borderRadius: '8px',
                    border: '1px solid #475569'
                  }}>
                    {formData.config.colorRanges.map((range, index) => (
                      <div key={range.id} style={{ 
                      display: 'flex',
                        gap: '1rem', 
                      alignItems: 'center',
                        marginBottom: index < formData.config.colorRanges.length - 1 ? '1rem' : '0',
                        padding: '0.75rem',
                        background: 'rgba(0, 0, 0, 0.1)',
                        borderRadius: '6px',
                        border: '1px solid #374151'
                      }}>
                        {/* Color Selection */}
                        <div style={{ minWidth: '120px' }}>
                          <Label style={{ fontSize: '11px', marginBottom: '0.25rem' }}>Color</Label>
                          <Select
                            value={range.color}
                            onChange={(e) => handleColorRangeChange(range.id, 'color', e.target.value)}
                            style={{ fontSize: '12px', padding: '0.5rem' }}
                          >
                            <option value="#10b981">🟢 Green (Normal)</option>
                            <option value="#f59e0b">🟡 Yellow (Warning)</option>
                            <option value="#ef4444">🔴 Red (Alarm)</option>
                            <option value="#3b82f6">🔵 Blue (Info)</option>
                            <option value="#8b5cf6">🟣 Purple (Custom)</option>
                            <option value="#f97316">🟠 Orange (Custom)</option>
                          </Select>
                    </div>

                        {/* Range Inputs */}
                        <div style={{ flex: 1 }}>
                          <Label style={{ fontSize: '11px', marginBottom: '0.25rem' }}>Range</Label>
                          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                            <input
                              type="number"
                              step="any"
                              value={range.min ?? ''}
                              onChange={(e) => handleColorRangeChange(range.id, 'min', e.target.value)}
                              placeholder="Min"
                              style={{ 
                                flex: 1, 
                                fontSize: '12px', 
                          padding: '0.5rem',
                                width: '100%',
                                border: '1px solid #475569',
                                borderRadius: '8px',
                                background: '#1e293b',
                                color: '#ffffff'
                              }}
                            />
                            <span style={{ color: '#94a3b8', fontSize: '12px' }}>to</span>
                            <input
                              type="number"
                              step="any"
                              value={range.max ?? ''}
                              onChange={(e) => handleColorRangeChange(range.id, 'max', e.target.value)}
                              placeholder="Max"
                              style={{ 
                                flex: 1, 
                                fontSize: '12px', 
                                padding: '0.5rem',
                                width: '100%',
                                border: '1px solid #475569',
                                borderRadius: '8px',
                                background: '#1e293b',
                                color: '#ffffff'
                              }}
                            />
                          </div>
                        </div>

                        {/* Remove Button */}
                        <button
                          type="button"
                          onClick={() => removeColorRange(range.id)}
                          style={{
                            background: '#ef4444',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                          padding: '0.5rem',
                          cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            minWidth: '32px',
                            height: '32px'
                          }}
                          title="Remove Range"
                        >
                          <XMarkIcon className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                    
                    {formData.config.colorRanges.length === 0 && (
                          <div style={{ 
                        textAlign: 'center', 
                        color: '#94a3b8', 
                        padding: '2rem',
                        fontStyle: 'italic'
                      }}>
                        No color ranges configured. Click "Add Range" to create one.
                          </div>
                    )}
                    
                          <div style={{ 
                      marginTop: '0.75rem', 
                      fontSize: '12px', 
                      color: '#94a3b8',
                      fontStyle: 'italic'
                    }}>
                      Example: Battery voltage - Green: 12.5-14.5V, Yellow: 11.5-12.5V, Red: 0-11.5V
                          </div>
                          </div>
                </FormSection>

                {showStatusRanges && (
                <FormSection>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                    <SectionTitle style={{ margin: 0 }}>Status Alerts</SectionTitle>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      {formData.dataSource?.includes('balancer') && (
                        <button
                          type="button"
                          onClick={applyBalancerStatusPresets}
                          style={{
                            background: '#334155',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            padding: '0.5rem 0.75rem',
                            fontSize: '12px',
                            cursor: 'pointer',
                          }}
                        >
                          Balancer presets
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={addStatusRange}
                        style={{
                          background: theme.colors.primary.main,
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          padding: '0.5rem 1rem',
                          fontSize: '12px',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.25rem',
                        }}
                      >
                        <PlusIcon className="w-4 h-4" />
                        Add Status
                      </button>
                    </div>
                  </div>
                  <div style={{
                    background: 'rgba(0, 0, 0, 0.2)',
                    padding: '1rem',
                    borderRadius: '8px',
                    border: '1px solid #475569',
                  }}>
                    {(formData.config.statusRanges || []).map((range, index) => (
                      <div
                        key={range.id}
                        style={{
                          display: 'grid',
                          gridTemplateColumns: '1fr 1fr 100px 120px auto',
                          gap: '0.75rem',
                          alignItems: 'end',
                          marginBottom: index < (formData.config.statusRanges || []).length - 1 ? '1rem' : '0',
                        }}
                      >
                        <FormGroup style={{ margin: 0 }}>
                          <Label>Status value</Label>
                          <Input
                            type="text"
                            value={range.match || ''}
                            onChange={(e) => handleStatusRangeChange(range.id, 'match', e.target.value)}
                            placeholder="e.g. imbalance"
                          />
                        </FormGroup>
                        <FormGroup style={{ margin: 0 }}>
                          <Label>Label</Label>
                          <Input
                            type="text"
                            value={range.label || ''}
                            onChange={(e) => handleStatusRangeChange(range.id, 'label', e.target.value)}
                            placeholder="Display label"
                          />
                        </FormGroup>
                        <FormGroup style={{ margin: 0 }}>
                          <Label>Color</Label>
                          <Input
                            type="color"
                            value={range.color || '#3b82f6'}
                            onChange={(e) => handleStatusRangeChange(range.id, 'color', e.target.value)}
                            style={{ height: '38px', padding: '2px' }}
                          />
                        </FormGroup>
                        <FormGroup style={{ margin: 0 }}>
                          <Label>Alarm</Label>
                          <select
                            value={range.alarm || 'none'}
                            onChange={(e) => handleStatusRangeChange(range.id, 'alarm', e.target.value)}
                            style={{
                              width: '100%',
                              padding: '0.5rem',
                              borderRadius: '6px',
                              border: '1px solid #475569',
                              background: '#1e293b',
                              color: 'white',
                            }}
                          >
                            <option value="none">None</option>
                            <option value="yellow">Yellow email</option>
                            <option value="red">Red email + NMEA</option>
                          </select>
                        </FormGroup>
                        <button
                          type="button"
                          onClick={() => removeStatusRange(range.id)}
                          style={{
                            background: '#ef4444',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            padding: '0.5rem',
                            cursor: 'pointer',
                            height: '38px',
                          }}
                          title="Remove status"
                        >
                          <XMarkIcon className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                    {(formData.config.statusRanges || []).length === 0 && (
                      <div style={{ textAlign: 'center', color: '#94a3b8', padding: '2rem', fontStyle: 'italic' }}>
                        No status rules yet. Use Balancer presets or Add Status.
                      </div>
                    )}
                    <div style={{ marginTop: '0.75rem', fontSize: '12px', color: '#94a3b8', fontStyle: 'italic' }}>
                      Text paths like balancer status: balanced (green), balancing (yellow), imbalance (red alarm).
                      Enable email alarms below for yellow/red status rules.
                    </div>
                  </div>
                </FormSection>
                )}

                {/* Alarm Configuration */}
                <FormSection>
                  <SectionTitle>Alarm Configuration</SectionTitle>
                  <div style={{ 
                    background: 'rgba(0, 0, 0, 0.2)', 
                    padding: '1rem', 
                    borderRadius: '8px',
                    border: '1px solid #475569'
                  }}>
                    <FormGroup>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                        <input
                          type="checkbox"
                          id="yellowAlarm"
                          checked={formData.config.yellowAlarm || false}
                          onChange={(e) => handleConfigChange('yellowAlarm', e.target.checked)}
                          style={{ margin: 0 }}
                        />
                        <Label htmlFor="yellowAlarm" style={{ margin: 0, fontSize: '14px' }}>
                          Send gentle email alarm when value enters a yellow range or yellow status
                        </Label>
                    </div>
                      <div style={{ fontSize: '12px', color: '#94a3b8', marginLeft: '1.5rem' }}>
                        Sends a gentle notification email when the widget value falls within any yellow color range or matches a status rule set to yellow
                  </div>
                    </FormGroup>

                    <FormGroup>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                        <input
                          type="checkbox"
                          id="redAlarm"
                          checked={formData.config.redAlarm || false}
                          onChange={(e) => handleConfigChange('redAlarm', e.target.checked)}
                          style={{ margin: 0 }}
                        />
                        <Label htmlFor="redAlarm" style={{ margin: 0, fontSize: '14px' }}>
                          Send urgent email alarm and raise NMEA 2000 alarm when value enters a red range or red status
                        </Label>
                      </div>
                      <div style={{ fontSize: '12px', color: '#94a3b8', marginLeft: '1.5rem' }}>
                        Sends an urgent notification email and raises a NMEA 2000 alarm when the value falls within any red color range or matches a status rule set to red (e.g. imbalance)
                      </div>
                    </FormGroup>

                    <FormGroup>
                      <Label>Email Recipients (comma-separated)</Label>
                      <Input
                        type="text"
                        value={formData.config.alarmEmails || ''}
                        onChange={(e) => handleConfigChange('alarmEmails', e.target.value)}
                        placeholder="e.g., captain@boat.com, crew@boat.com"
                        style={{ fontSize: '12px' }}
                      />
                      <div style={{ fontSize: '12px', color: '#94a3b8', marginTop: '0.25rem' }}>
                        Leave empty to use the vessel owner's email address
                </div>
                    </FormGroup>
                  </div>
                </FormSection>
              </form>
            </FormContainer>
          )}

          <DataBrowserContainer $hidden={activeTab !== 'browse'}>
            <DataBrowser
              vesselId={vesselId}
              onPathSelect={handleDataPathSelect}
              selectedPaths={formData.dataSource ? [formData.dataSource] : []}
              dataSource="database"
              pickerMode
              dark
              onShowCatalogTab={() => openPathPicker('catalog')}
            />
          </DataBrowserContainer>

          <DataBrowserContainer $hidden={activeTab !== 'catalog'}>
            <SignalKCatalogPicker
              vesselId={vesselId}
              selectionMode="single"
              dark
              selectedPaths={formData.dataSource ? [formData.dataSource] : []}
              onPathSelect={(path) => handleDataPathSelect(path, {})}
              hint="Every path the boat has reported — search by name, filter by status, then Use path."
            />
          </DataBrowserContainer>
        </TabContent>

        <ButtonGroup>
          <Button type="button" onClick={onClose}>
            Cancel
          </Button>
          {(activeTab === 'browse' || activeTab === 'catalog') && formData.dataSource ? (
            <Button type="button" onClick={() => setActiveTab('settings')} variant="primary">
              Continue — Save Widget
            </Button>
          ) : (
            <Button
              type="button"
              onClick={(e) => handleSubmit(e)}
              variant="primary"
              disabled={!formData.dataSource}
            >
              Save Widget
            </Button>
          )}
        </ButtonGroup>
      </ModalContent>
    </ModalOverlay>
  );
};

export default WidgetEditor;